import React, { useState, useMemo, useCallback } from "react";
import {
  ClusterWrap,
  FormContainerItem,
  InformationBanner,
  SecondaryButton,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  TextButton,
  Th,
  TitleWrap,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import {
  ElemType,
  TargetField as TargetFieldType,
} from "../../../../../application/domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import { TargetFieldModal } from "./targetFieldModal";

interface TargetFieldsProps {
  targetFields: TargetFieldType[];
  editingField: TargetFieldType | undefined;
  setEditingField: (field: TargetFieldType | undefined) => void;
  handleDeleteField: (elemId: string) => void;
  handleSaveField: (field: TargetFieldType) => boolean;
  isTargetFieldUsedInRules: (elemId: string) => boolean;
  errorMessage?: string;
}

export const TargetFields: React.FC<TargetFieldsProps> = ({
  targetFields,
  editingField,
  setEditingField,
  handleDeleteField,
  handleSaveField,
  isTargetFieldUsedInRules,
  errorMessage,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const displayNames = useMemo<Record<ElemType, string>>(
    () => ({
      radio_buttons: "ラジオボタン",
      select: "セレクトボックス",
      checkboxes: "チェックボックス",
    }),
    [],
  );

  const getElemTypeDisplayName = (elemType: ElemType): string => {
    return displayNames[elemType];
  };

  const openAddModal = useCallback(() => {
    setEditingField(undefined);
    setIsModalOpen(true);
  }, [setEditingField, setIsModalOpen]);

  const openEditModal = useCallback(
    (elemId: string) => {
      const field = targetFields.find((f) => f.elemId === elemId);
      if (field) {
        setEditingField(field);
        setIsModalOpen(true);
      }
    },
    [targetFields, setEditingField, setIsModalOpen],
  );

  const onSaveField = useCallback(
    (field: TargetFieldType) => {
      const success = handleSaveField(field);
      if (success) {
        setIsModalOpen(false);
      }
    },
    [handleSaveField, setIsModalOpen],
  );

  const existingIds = useMemo(
    () => targetFields.map((field) => field.elemId),
    [targetFields],
  );

  return (
    <>
      <FormContainerItem>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <TitleWrap
              id="target-fields-title"
              title="選択項目一覧"
              description={`振り分けルールの条件として用いる「選択項目」を設定して下さい\n既に振り分けルールの条件として用いられている選択項目は編集、削除できません。`}
              required
            />
          </div>
          <SecondaryButton
            text="選択項目追加"
            onClick={openAddModal}
            icon="PlusCircleIcon"
          />
        </div>
      </FormContainerItem>
      <FormContainerItem>
        {!errorMessage && targetFields?.length === 0 && (
          <div className="py-2">
            <InformationBanner
              message="選択項目がまだ登録されていません。"
              description="「選択項目追加」より、選択項目を追加してください。"
            />
          </div>
        )}
        {errorMessage && (
          <div className="py-2">
            <WarningBanner message={errorMessage} />
          </div>
        )}
        {targetFields?.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <Th>ID</Th>
                <Th>項目の説明</Th>
                <Th>項目タイプ</Th>
                <Th>期待される選択肢</Th>
                <Th>アクション</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {targetFields.map((t) => (
                <TableRow key={t.elemId}>
                  <Td>{t.elemId}</Td>
                  <Td enabledEllipsis>{t.description}</Td>
                  <Td>{getElemTypeDisplayName(t.elemType)}</Td>
                  <Td enabledEllipsis>{t.expectedValues.join(", ")}</Td>
                  <Td>
                    <ClusterWrap>
                      <TextButton
                        text="編集"
                        icon="EditIcon"
                        onClick={() => openEditModal(t.elemId)}
                        disabled={isTargetFieldUsedInRules(t.elemId)}
                      />
                      <TextButton
                        text="削除"
                        color="danger"
                        icon="TrashIcon"
                        onClick={() => handleDeleteField(t.elemId)}
                        disabled={isTargetFieldUsedInRules(t.elemId)}
                      />
                    </ClusterWrap>
                  </Td>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </FormContainerItem>

      <TargetFieldModal
        isOpen={isModalOpen}
        onClose={useCallback(() => setIsModalOpen(false), [setIsModalOpen])}
        onSave={onSaveField}
        initialData={editingField}
        existingIds={existingIds}
      />
    </>
  );
};
